import { BaseButton } from "@/shared/ui/BaseButton";
import { RequiredBadge } from "@/shared/ui/base-badge";
import { BaseDialog } from "@/shared/ui/base-dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { OfferCargoRejectParams } from "../../pages/shipper-delivery-proposal-detail/model";

const paramsSchema = z.object({
  reason: z.enum(
    ["chose_another_offer", "shipping_fee", "schedule", "others"],
    {
      required_error: "見送る理由を選択してください",
    },
  ),
  details: z.string().optional(),
});

export type ContractProposalRejectionDialogProps = {
  open: boolean;
  onSubmit: (params: OfferCargoRejectParams) => void;
  onClose: () => void;
};
export const ContractProposalRejectionDialog: React.FC<
  ContractProposalRejectionDialogProps
> = ({ open, onSubmit, onClose }) => {
  const form = useForm<z.infer<typeof paramsSchema>>({
    resolver: zodResolver(paramsSchema),
    defaultValues: {},
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const reason = e.target.value;
    if (
      reason !== "chose_another_offer" &&
      reason !== "shipping_fee" &&
      reason !== "schedule" &&
      reason !== "others"
    ) {
      console.error("[RejectionReasonDialog] invalid reason value", reason);
      return;
    }
    form.setValue("reason", reason);
  };
  const handleDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    form.setValue("details", e.target.value);
  };
  const handleRejectClick = () => {
    form.handleSubmit(onSubmit)();
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      aria-labelledby="rejection-reason-dialog"
    >
      <DialogTitle id="rejection-reason-dialog">
        この契約を見送りますか？
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Stack spacing={0}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontWeight={500}>
                  1.見送る理由を選択してください
                </Typography>
                <RequiredBadge />
              </Stack>
              <Typography variant="caption" color="grey.500">
                ※荷主に表示されます。
              </Typography>
            </Stack>
            <FormControl>
              <RadioGroup
                aria-labelledby="radio-buttons-group-label"
                onChange={handleReasonChange}
              >
                <FormControlLabel
                  value="chose_another_offer"
                  control={<Radio />}
                  label="他に荷物が見つかったため"
                />
                <FormControlLabel
                  value="shipping_fee"
                  control={<Radio />}
                  label="運賃が条件と合わないため"
                />
                <FormControlLabel
                  value="schedule"
                  control={<Radio />}
                  label="積込/取卸の日時が条件と合わないため"
                />
                <FormControlLabel
                  value="others"
                  control={<Radio />}
                  label="その他"
                />
              </RadioGroup>
              <FormHelperText error>
                {form.formState.errors.reason?.message}
              </FormHelperText>
            </FormControl>
          </Stack>
          <Stack spacing={1}>
            <Stack spacing={0}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontWeight={500}>
                  2.コメントを入力してください(任意)
                </Typography>
              </Stack>
              <Typography variant="caption" color="grey.500">
                ※荷主に表示されます。
              </Typography>
            </Stack>
            <TextField
              margin="dense"
              id="rejection-details"
              placeholder="捕捉事項がございましたらご記入ください"
              type="text"
              fullWidth
              multiline
              rows={4}
              onChange={handleDetailsChange}
            />
          </Stack>
        </Stack>
        <Stack spacing={2} p={2} alignItems="center">
          <Typography variant="body2">
            ※ 見送った場合、取り消しできません
          </Typography>
          <Stack direction="row" spacing={2}>
            <BaseButton variant="outlined" onClick={onClose} color="primary">
              キャンセル
            </BaseButton>
            <BaseButton
              variant="contained"
              onClick={handleRejectClick}
              color="primary"
            >
              見送る
            </BaseButton>
          </Stack>
        </Stack>
      </DialogContent>
    </BaseDialog>
  );
};
